import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function BecomeHost() {
  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Earn Extra Income',
      description: 'Turn your unused space into a steady income stream. Hosts earn an average of $500-$2000 per month.'
    },
    {
      icon: 'ri-calendar-line',
      title: 'You\'re in Control',
      description: 'Set your own prices, availability, and house rules. Accept or decline booking requests as you wish.'
    },
    {
      icon: 'ri-customer-service-line',
      title: '24/7 Support',
      description: 'Our dedicated host support team is always here to help you succeed.'
    },
    {
      icon: 'ri-shield-star-line',
      title: 'Verified Renters',
      description: 'All renters are verified with ID checks and reviews, so you know exactly who\'s using your space.'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Create Your Listing',
      description: 'Tell us about your space with photos and details. It takes about 15 minutes.',
      icon: 'ri-file-list-line'
    },
    {
      number: '2',
      title: 'Set Your Terms',
      description: 'Choose your pricing, availability, and any special requirements.',
      icon: 'ri-settings-line'
    },
    {
      number: '3',
      title: 'Start Hosting',
      description: 'Once approved, your listing goes live and you can start accepting bookings.',
      icon: 'ri-rocket-line'
    }
  ];

  const earnings = [
    { space: 'Garage Storage', monthly: '$200-500', icon: 'ri-home-4-line' },
    { space: 'Driveway Parking', monthly: '$100-200', icon: 'ri-car-line' },
    { space: 'Workshop Space', monthly: '$300-700', icon: 'ri-tools-line' },
    { space: 'Jetty/Pontoon', monthly: '$400-1000', icon: 'ri-ship-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <div className="relative bg-gradient-to-br from-blue-600 to-blue-800 py-20 pt-36">
        <div className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=Australian%20suburban%20homes%20with%20garages%20and%20driveways%2C%20aerial%20view%20showing%20residential%20properties%20with%20storage%20spaces%20and%20parking%20areas%2C%20bright%20sunny%20day%20with%20clear%20blue%20sky%2C%20professional%20real%20estate%20photography%20highlighting%20property%20features&width=1920&height=600&seq=host-hero&orientation=landscape)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold text-white mb-6">
              Earn money from your unused space
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Join thousands of Australians who are earning extra income by sharing their garages, driveways, storage spaces, and more on STASHLY.
            </p>
            <Link
              to="/list-space"
              className="inline-block px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:bg-gray-50 transition-colors shadow-lg cursor-pointer whitespace-nowrap text-lg"
            >
              <i className="ri-add-circle-line mr-2"></i>
              List Your Space Now
            </Link>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Host on STASHLY?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We make it easy and safe to share your space
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-md">
                <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-3xl text-blue-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How to Get Started
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Start earning in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-24 h-24 mx-auto mb-6 bg-blue-600 rounded-full flex items-center justify-center">
                  <i className={`${step.icon} text-4xl text-white`}></i>
                </div>
                <div className="text-5xl font-bold text-gray-200 mb-4">
                  {step.number}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Earnings Calculator */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Potential Earnings
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See how much you could earn based on your space type
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {earnings.map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-2xl shadow-md text-center">
                <div className="w-16 h-16 mx-auto bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-3xl text-green-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {item.space}
                </h3>
                <div className="text-3xl font-bold text-green-600 mb-1">
                  {item.monthly}
                </div>
                <p className="text-sm text-gray-600">per month</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <a
              href="/pricing#calculator"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#2563eb] font-bold text-lg rounded-full hover:bg-gray-50 transition-colors shadow-lg cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calculator-line text-xl"></i>
              Calculate Your Earnings
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to start earning?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            List your space today and join Australia's fastest-growing sharing economy
          </p>
          <Link
            to="/list-space"
            className="inline-block px-10 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:bg-gray-50 transition-colors shadow-lg cursor-pointer whitespace-nowrap text-lg"
          >
            <i className="ri-add-circle-line mr-2"></i>
            Create Your Listing
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
